import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from './TanTable';
import ProDetailSlideOver from './pro/ProDetailSlideOver';
import userHistoryService, { UserHistoryRow } from '../services/userHistory.service';
import activityService, { UserActivityRow } from '../services/activity.service';
import impersonationService, { UserSearchResult } from '../services/impersonation.service';

interface UserHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Developer group — shows the cross-user picker. */
  isAdmin?: boolean;
}

type TabId = 'history' | 'activity';

const formatDateTime = (value: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

const formatDuration = (ms: number | null): string => {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

/** Truncated cell with the full value available on hover. */
const TruncatedCell: React.FC<{ value: string }> = ({ value }) => (
  <span className="block max-w-[16rem] truncate text-sm" title={value}>
    {value}
  </span>
);

/** Compact admin-only user picker (searches azure_ad_users like the
 *  impersonation modal). Selecting a user applies to both tabs. */
const AdminUserPicker: React.FC<{
  selectedUser: UserSearchResult | null;
  onSelect: (user: UserSearchResult | null) => void;
}> = ({ selectedUser, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const found = await impersonationService.searchUsers(query);
        // Real staff only — the directory contains external/test accounts.
        setResults(found.filter((u) => u.email?.toLowerCase().endsWith('@gwii.com')));
        setOpen(true);
      } catch {
        setResults([]);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  if (selectedUser) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="px-2 py-1 rounded bg-slate-600 text-white">
          {selectedUser.displayName}
        </span>
        <button
          onClick={() => onSelect(null)}
          className="text-slate-300 hover:text-white underline"
          aria-label="View my own data"
        >
          back to me
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder="View another user..."
        aria-label="Search users"
        className="px-2 py-1 text-sm rounded bg-slate-600 text-white placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-400 w-48"
      />
      {open && results.length > 0 && (
        // z must beat TanTable's internal z-[9999] sticky/toolbar layers
        <ul className="absolute right-0 top-full mt-1 w-72 max-h-64 overflow-y-auto rounded-md shadow-lg bg-panel border border-gray-200 dark:border-gray-700 z-[10000]">
          {results.map((u) => (
            <li key={u.id}>
              <button
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-body"
                onClick={() => {
                  onSelect(u);
                  setQuery('');
                  setResults([]);
                  setOpen(false);
                }}
              >
                <span className="font-medium">{u.displayName}</span>
                <span className="block text-xs text-muted">{u.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const UserHistoryModal: React.FC<UserHistoryModalProps> = ({ isOpen, onClose, isAdmin = false }) => {
  const [activeTab, setActiveTab] = useState<TabId>('history');
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);

  const [historyRows, setHistoryRows] = useState<UserHistoryRow[]>([]);
  // One window per tab. They are different numbers from different sources (cms.history
  // vs user_activity) and each server decides its own, so a single `days` would have
  // whichever tab loaded last titling the other one.
  const [days, setDays] = useState(3);
  const [activityDays, setActivityDays] = useState(7);
  const [activityRows, setActivityRows] = useState<UserActivityRow[]>([]);
  const [loadedKeys, setLoadedKeys] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedProNumber, setSelectedProNumber] = useState<number | null>(null);

  // Lazy fetch: each (tab, user) pair loads on first view; reloadKey retries.
  const fetchKey = `${activeTab}:${selectedUser?.id ?? 'me'}:${reloadKey}`;
  useEffect(() => {
    if (!isOpen || loadedKeys.has(fetchKey)) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const load = async () => {
      if (activeTab === 'history') {
        const res = selectedUser
          ? await userHistoryService.getUserHistory(selectedUser.id, controller.signal)
          : await userHistoryService.getMyHistory(controller.signal);
        setHistoryRows(res.items);
        setDays(res.days);
      } else {
        const res = selectedUser
          ? await activityService.getUserActivity(selectedUser.id, controller.signal)
          : await activityService.getMyActivity(controller.signal);
        setActivityRows(res.items);
        setActivityDays(res.days);
      }
    };

    load()
      .then(() => setLoadedKeys((prev) => new Set(prev).add(fetchKey)))
      .catch((err: any) => {
        if (controller.signal.aborted) return;
        setError(err.response?.data?.detail || 'Failed to load data');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [isOpen, fetchKey]);

  // Switching users invalidates everything loaded for the previous user.
  const handleSelectUser = (user: UserSearchResult | null) => {
    setSelectedUser(user);
    setLoadedKeys(new Set());
    setHistoryRows([]);
    setActivityRows([]);
  };

  const proLinkCell = (value: string) => {
    const pro = Number(value);
    if (Number.isFinite(pro) && pro > 0) {
      return (
        <button
          onClick={() => setSelectedProNumber(pro)}
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
          title={`Open PRO ${pro}`}
        >
          {value}
        </button>
      );
    }
    return <span className="text-sm">{value}</span>;
  };

  const historyColumns = useMemo<ColumnDef<UserHistoryRow>[]>(() => [
    {
      accessorKey: 'datetime',
      header: 'Date/Time',
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ getValue }) => (
        <span className="text-sm whitespace-nowrap">{formatDateTime(getValue<string>())}</span>
      ),
    },
    {
      accessorKey: 'dbtable',
      header: 'Table',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
    },
    {
      accessorKey: 'keyvalue',
      header: 'Key',
      enableSorting: true,
      enableColumnFilter: true,
      // keyvalue is always a PRO number in cms.history — link it to the pro
      // drawer (numeric guard just protects against blank/odd legacy rows).
      cell: ({ getValue }) => proLinkCell(getValue<string>()),
    },
    {
      accessorKey: 'colname',
      header: 'Column',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
    },
    {
      accessorKey: 'oldval',
      header: 'Old Value',
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ getValue }) => <TruncatedCell value={getValue<string>()} />,
    },
    {
      accessorKey: 'newval',
      header: 'New Value',
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ getValue }) => <TruncatedCell value={getValue<string>()} />,
    },
    {
      accessorKey: 'type',
      header: 'Type',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
    },
    {
      accessorKey: 'otherid',
      header: 'Other ID',
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ getValue }) => <TruncatedCell value={getValue<string>()} />,
    },
    {
      accessorKey: 'userid',
      header: 'User ID',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
    },
  ], []);

  const activityColumns = useMemo<ColumnDef<UserActivityRow>[]>(() => [
    {
      accessorKey: 'occurred_at',
      header: 'Date/Time',
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ getValue }) => (
        <span className="text-sm whitespace-nowrap">{formatDateTime(getValue<string>())}</span>
      ),
    },
    {
      accessorKey: 'event_type',
      header: 'Event',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
    },
    {
      accessorKey: 'page_path',
      header: 'Page',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
    },
    {
      accessorKey: 'target',
      header: 'Target',
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ getValue }) => <TruncatedCell value={getValue<string>() ?? ''} />,
    },
    {
      accessorKey: 'detail',
      header: 'Detail',
      enableSorting: true,
      enableColumnFilter: true,
      // pro_view details are pronumbers — link them like the history tab.
      cell: ({ row, getValue }) => {
        const value = getValue<string>() ?? '';
        if (row.original.event_type === 'pro_view') return proLinkCell(value);
        return <TruncatedCell value={value} />;
      },
    },
    {
      accessorKey: 'duration_ms',
      header: 'Duration',
      enableSorting: true,
      enableColumnFilter: false,
      cell: ({ getValue }) => (
        <span className="text-sm whitespace-nowrap">{formatDuration(getValue<number | null>())}</span>
      ),
    },
  ], []);

  // Read-only history: Alt+C closes; the layer keeps Alt+S off the grid below.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  const titleUser = selectedUser ? selectedUser.displayName : 'My';
  const tabClass = (tab: TabId) =>
    `px-4 py-1.5 text-sm rounded-t-md transition-colors ${
      activeTab === tab
        ? 'bg-white dark:bg-gray-900 text-slate-800 dark:text-gray-100 font-medium'
        : 'bg-slate-600 text-slate-200 hover:bg-slate-500'
    }`;

  return createPortal(
    // z-[100] matches ProDetailSlideOver: portaled to body this still covers
    // the nav (z-100 inside #root, earlier in DOM), while the pro drawer
    // (same z, mounted later) correctly covers the modal.
    <div className="fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-[100]">
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-[95vw] h-[90vh] mx-4 overflow-hidden flex flex-col"
      >
        {/* Header — tabs inline with the admin picker and close button;
            the table's cardTitle carries the "who/window" text instead.
            Already the app-standard slate bar; it doubles as the drag handle
            (the hook ignores drags starting on its buttons/inputs). */}
        <div
          {...dragHandleProps}
          className="bg-slate-700 text-white px-6 pt-2 shrink-0 flex items-end justify-between cursor-move select-none touch-none"
        >
          <div className="flex gap-1">
            <button onClick={() => setActiveTab('history')} className={tabClass('history')}>
              User History
            </button>
            <button onClick={() => setActiveTab('activity')} className={tabClass('activity')}>
              User Activity
            </button>
          </div>
          <div className="flex items-center gap-4 pb-2">
            {isAdmin && (
              <AdminUserPicker selectedUser={selectedUser} onSelect={handleSelectUser} />
            )}
            <button
              onClick={onClose}
              aria-label="Close My History"
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 p-3 flex flex-col">
          {error ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={() => setReloadKey((k) => k + 1)}
                className="px-4 py-2 text-sm rounded-lg bg-slate-700 text-white hover:bg-slate-600 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : activeTab === 'history' ? (
            <TanTable
              // Distinct key per tab: the History and Activity grids share this ternary
              // slot with different settingsKeys, so without a key React reuses ONE
              // TanTable instance across the tab switch and only swaps settingsKey. Since
              // TanTable loads settings once per mount, its debounced autosave then writes
              // the stale layout into the other tab's key, wiping it. Key = fresh mount
              // per tab; seedLayoutFromCache applies the cached layout pre-paint (no flash).
              key="user-history-modal"
              data={historyRows}
              columns={historyColumns}
              loading={loading}
              settingsKey="user-history-modal"
              seedLayoutFromCache
              cardTitle={`${titleUser} History — Last ${days} Days`}
              searchPlaceholder="Search history..."
              enableColumnFilters={true}
              columnFilterTypes={{
                datetime: 'text',
                keyvalue: 'text',
                oldval: 'text',
                newval: 'text',
                otherid: 'text',
              }}
              enableSearch={true}
              enableExcelExport={true}
              defaultSorting={[{ id: 'datetime', desc: true }]}
              fillContainer={true}
            />
          ) : (
            <TanTable
              // See the History grid above — distinct key keeps this Activity grid a
              // separate instance so the two tabs never cross-write each other's layout.
              key="user-activity-modal"
              data={activityRows}
              columns={activityColumns}
              loading={loading}
              settingsKey="user-activity-modal"
              seedLayoutFromCache
              cardTitle={`${titleUser} Activity — Last ${activityDays} Days`}
              searchPlaceholder="Search activity..."
              enableColumnFilters={true}
              columnFilterTypes={{
                occurred_at: 'text',
                target: 'text',
                detail: 'text',
              }}
              enableSearch={true}
              enableExcelExport={true}
              defaultSorting={[{ id: 'occurred_at', desc: true }]}
              fillContainer={true}
            />
          )}
        </div>
      </div>

      {/* Pro drawer — portals itself to body after the modal, so same-z DOM
          order puts it on top. Mounted inside the modal so closing the modal
          also closes the drawer. */}
      <ProDetailSlideOver
        pronumber={selectedProNumber}
        isOpen={selectedProNumber !== null}
        onClose={() => setSelectedProNumber(null)}
      />
    </div>,
    document.body
  );
};

export default UserHistoryModal;
